import { asRelativeDate } from "@/utils/format";
import { sanitizeStudyRoute } from "@/utils/study-route";
import { useLocation } from "wouter";
import { useModalStore } from "@/stores/use-modal-store";
import { Study } from "@/api/studio/studies/types/study.type";
import { Project } from "@/api/studio/projects/types/project.type";
import { useGetProjects } from "@/api/studio/projects/queries";
import { useGetAccountStudies } from "@/api/studio/studies/queries";
import { HomeLayout } from "@/components/layouts";
import { CreateProjectModal } from "@/features/projects/modals/create-project-modal";
import { RecentStudiesSkeleton } from "@/features/studies/components/recent-studies-skeleton";
import { CardBadge } from "@/components/ui/card/card-badge";
import { useGetSession } from "@/api/auth/queries";
import { FolderIcon, LockClosedIcon, PlusIcon, UserIcon, UsersIcon } from "@heroicons/react/24/solid";
import { StudyDropdown } from "@/features/studies/components/study-dropdown";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardSkeleton } from "@/components/ui/card";
import { Error } from "@/components/fallbacks/error-boundaries/error";
import { Table, TableHeader, TableRow, TableCell } from "@/components/ui/table";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { StudyIcon } from "@/features/studies/components/study-icon";
import { dynamicUseCase } from "@/utils/dynamic-use-case";
import { projectThumbnails } from "@/assets/project-thumbnails";
import { EmptyState } from "@/features/projects/components/empty-state";
import { NavLink } from "@/components/ui/navigation";
import { TextSkeleton } from "@/features/projects/components/text-skeleton";
import { useSegment } from "@/hooks/use-segment";
import { useEffect } from "react";
import { useGlobalStore } from "@/stores/global-store";

/*
 * HomeRoute component
 */
export const HomeRoute = () => {
  /* Stores */
  const [, navigate] = useLocation();
  const { open } = useModalStore((state) => state.modalActions);
  const theme = useGlobalStore((state) => state.theme);

  /* Hooks */
  const { segmentPageEvent } = useSegment();

  /* Queries */
  const { data: session, isLoading: isSessionLoading } = useGetSession();
  const {
    data: recentStudies,
    isLoading: isRecentStudiesLoading,
    isError: isRecentStudiesError,
    refetch: refetchRecentStudies,
  } = useGetAccountStudies({ query: "", pageIndex: 1, itemsPerPage: 10, sortBy: "desc", orderBy: "updatedAt" });
  const {
    data: recentProjects,
    isLoading: isRecentProjectsLoading,
    isError: isRecentProjectsError,
    refetch: refetchRecentProjects,
  } = useGetProjects({ query: "", pageIndex: 1, itemsPerPage: 10, sortBy: "desc", orderBy: "updatedAt" });

  /* Handlers */
  const handleProjectOpen = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleStudyOpen = (study: Study) => {
    const validRoute = sanitizeStudyRoute(study.route);
    navigate(`/projects/${study.project.id}/studies/${study.id}/${study.type}/${validRoute}`);
  };

  const handleNew = () => {
    open(<CreateProjectModal />);
  };

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the home page
    if (theme) segmentPageEvent("Home", { title: "Home | Dragonfly AI Studio", theme: theme });
  }, [theme, segmentPageEvent]);

  /* Render */
  return (
    <HomeLayout>
      {/* Primary */}
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">{!isSessionLoading && session ? `Welcome ${session?.user.firstName}` : isSessionLoading ? <TextSkeleton variant="h1" /> : "Welcome"}</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" onClick={handleNew}>
            <PlusIcon className="size-5" /> New project
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Secondary */}
      <Toolbar>
        <ToolbarItem>
          <h2 className="font-semibold">Recent projects</h2>
          <NavLink href="/projects">View all</NavLink>
        </ToolbarItem>
      </Toolbar>

      {/* Projects */}
      <div className="max-w-full overflow-hidden">
        <div className="w-full flex flex-row overflow-x-scroll gap-4">
          {isRecentProjectsLoading && Array.from({ length: 5 }).map((_, index) => <CardSkeleton key={index} />)}
          {isRecentProjectsError && <Error onRefresh={refetchRecentProjects} showHomeBtn={false} />}
          {recentProjects && recentProjects.projects.length === 0 && <EmptyState title="You have no projects yet" isProjectEmpty={true} className="w-full" />}
          {recentProjects &&
            recentProjects.projects.map((project) => (
              <Card key={project.id} imageUrl={projectThumbnails[project.thumbnailName]} onClick={() => handleProjectOpen(project)}>
                <CardBody title={project.name}>
                  <p className="text-base-content/60">
                    {project.studyCount} {project.studyCount != 1 ? "studies" : "study"}
                  </p>
                </CardBody>
                <CardBadge>
                  {project.visibility === "private" && (
                    <>
                      <LockClosedIcon className="size-3.5" /> Private{" "}
                    </>
                  )}
                  {project.visibility === "public" && (
                    <>
                      <UsersIcon className="size-3.5" /> Shared
                    </>
                  )}
                </CardBadge>
              </Card>
            ))}
        </div>
      </div>

      {/* Studies */}
      {recentProjects && recentProjects.projects.length !== 0 && (
        <div className="grow flex-1 flex flex-col gap-7">
          <div className="flex flex-row gap-2 items-center">
            <h2 className="font-semibold">Recent studies</h2>
            <NavLink href="/studies">View all</NavLink>
          </div>
          {isRecentStudiesLoading && <RecentStudiesSkeleton />}
          {isRecentStudiesError && <Error onRefresh={refetchRecentStudies} showHomeBtn={false} />}
          {recentStudies && (
            <Table>
              <thead>
                <TableRow>
                  <TableHeader className="w-80">Study</TableHeader>
                  <TableHeader>Project</TableHeader>
                  <TableHeader>Use case</TableHeader>
                  <TableHeader>Author</TableHeader>
                  <TableHeader>Last edited</TableHeader>
                  <TableHeader className="w-40"></TableHeader>
                </TableRow>
              </thead>
              <tbody>
                {recentStudies.studies.map((study) => (
                  <TableRow key={study.id} onClick={() => handleStudyOpen(study)}>
                    <TableCell>
                      <StudyIcon useCase={study.useCase} />
                      <div className="flex flex-col gap-1 truncate">
                        <p className="font-semibold text-sm truncate">{study.name}</p>
                        <p className="text-sm text-base-content/60 truncate">
                          {study.assetCount} {study.assetCount === 1 ? study.type : `${study.type}s`}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <FolderIcon className="size-4 shrink-0" />
                      <p className="truncate text-sm">{study.project.name}</p>
                    </TableCell>
                    <TableCell>
                      {dynamicUseCase(study.useCase.name)}
                      <p className="text-sm truncate">{study.useCase.name}</p>
                    </TableCell>
                    <TableCell>
                      <UserIcon className="size-4 shrink-0" /> <p className="text-sm truncate">{study.username}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm truncate">{asRelativeDate(study.updatedAt)}</p>
                    </TableCell>
                    <TableCell className="justify-end" truncate={false}>
                      <StudyDropdown projectId={study.project.id} study={study} />
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}
    </HomeLayout>
  );
};
